import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import * as Cons from 'data/Consignment';
import Comment from 'data/Comment';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import Button from 'components/UI/Button';
import Divider from 'components/UI/Divider';
import Pill from 'components/UI/Pill';
import WarningBanner from 'components/UI/WarningBanner';
import Icon from 'view/Icon';
import { useCountries } from 'hooks/useCountries';
import { getCurrencyIsoByCountry, getCurrencySymbol } from 'utils/country';
import { formatDateTime, getLocalTimezone } from 'utils/formatters';
import { isVendorComment } from 'utils/comment';
import { EDIT_ORDER_ACTIONS } from 'constants/product';
import { TEXT_LABELS } from 'constants/order';
import { PermissionCode } from 'data/UserPermissions';
import ConfirmationModal from 'components/UI/ConfimationModal';
import { renderToast, toast } from 'components/UI/ToastNotification';
import { useAPI } from 'lib/API';
import { withdrawEditApproval, resendEditApproval, approveEditOrderOnBehalf } from 'requests/consignment';
import { BadgeColor, Background, Border, Main, Text } from 'styles/themeColors';
import { Space } from 'styles/themeSpaces';
import { BorderRadius } from 'styles/themeBorderRadius';
import {
  SectionCard,
  SectionTitle,
  SectionSubtitle,
  TableWrapper,
  Table,
  HeaderCell,
  BodyCell,
  ProductImage,
  ProductName,
  SkuText,
  TotalLabel,
  TotalValue
} from 'components/Orders/EditOrder/EditOrder.styles';

const latestVendorComment = (comments?: Comment[]): string | undefined =>
  (comments || [])
    .filter(isVendorComment)
    .pop()?.comment;

type LiveEntry = Cons.ConsignmentDetails['consignmentEntries'][number];

const sameProductOrUnknown = (entry: LiveEntry, snapshot: Cons.ConsignmentSnapshotEntry) =>
  entry.productId == null || snapshot.productId == null || entry.productId === snapshot.productId;

const isChangedFromSnapshot = (entry: LiveEntry, snapshot: Cons.ConsignmentSnapshotEntry) => {
  const comment = latestVendorComment(entry.comments)?.trim();
  return Number(entry.quantity) !== Number(snapshot.quantity)
    || Number(entry.basePrice) !== Number(snapshot.basePrice)
    || (!!comment && comment !== snapshot.comment?.trim());
};

const changeTypeOf = (entry: LiveEntry, snapshot?: Cons.ConsignmentSnapshotEntry) => {
  if (!snapshot) return EDIT_ORDER_ACTIONS.ADDED;
  const marker = entry.modificationType;
  if (marker === EDIT_ORDER_ACTIONS.REMOVED || marker === EDIT_ORDER_ACTIONS.ADDED || marker === EDIT_ORDER_ACTIONS.CHANGED) return marker;
  return isChangedFromSnapshot(entry, snapshot) ? EDIT_ORDER_ACTIONS.CHANGED : undefined;
};

const deriveSubmittedChanges = (liveEntries: LiveEntry[], snapshotEntries: Cons.ConsignmentSnapshotEntry[]) => {
  if (!snapshotEntries.length) {
    return liveEntries
      .filter(entry => entry.modificationType && entry.modificationType !== EDIT_ORDER_ACTIONS.UNCHANGED)
      .map(entry => ({ ...entry, key: `live-${entry.id}`, vendorComment: latestVendorComment(entry.comments) }));
  }

  const unpaired = new Map(snapshotEntries.map(snapshot => [snapshot.id, snapshot]));
  const take = (predicate: (snapshot: Cons.ConsignmentSnapshotEntry) => boolean) => {
    const match = Array.from(unpaired.values()).find(predicate);
    if (match) unpaired.delete(match.id);
    return match;
  };

  const pairs = liveEntries.map(entry => ({
    entry,
    snapshot: take(snapshot => snapshot.consignmentEntryId === entry.id && sameProductOrUnknown(entry, snapshot))
  }));
  pairs.forEach(pair => {
    if (!pair.snapshot && pair.entry.entryNumber != null) {
      pair.snapshot = take(snapshot => snapshot.entryNumber === pair.entry.entryNumber
        && pair.entry.productId != null && snapshot.productId === pair.entry.productId);
    }
  });

  const changedLiveEntries = pairs.flatMap(({ entry, snapshot }) => {
    const modificationType = changeTypeOf(entry, snapshot);
    return modificationType
      ? [{ ...entry, modificationType, key: `live-${entry.id}`, vendorComment: latestVendorComment(entry.comments) }]
      : [];
  });
  const removedEntries = Array.from(unpaired.values())
    .filter(snapshot => snapshot.parentEntryNumber == null)
    .map(snapshot => ({
      ...snapshot,
      id: snapshot.consignmentEntryId,
      key: `snapshot-${snapshot.id}`,
      modificationType: EDIT_ORDER_ACTIONS.REMOVED,
      vendorComment: latestVendorComment(snapshot.comments)
    }));

  return [...changedLiveEntries, ...removedEntries];
};

// Read-only twin of the EditOrderItemsTable tag — same variants, no editing states
const TypeTag = styled.span<{ $variant?: 'default' | 'addition' | 'removal' | 'updated' }>`
  display: inline-block;
  border: 1px solid ${({ $variant }) => $variant === 'default' || !$variant ? Border.main : 'transparent'};
  border-radius: ${BorderRadius.s};
  background-color: ${({ $variant }) => $variant === 'addition'
    ? `${Main.success}1A`
    : $variant === 'removal'
      ? `${Main.error}1A`
      : $variant === 'updated'
        ? `${BadgeColor.blue}1A`
        : 'transparent'};
  color: ${({ $variant }) => $variant === 'addition'
    ? Main.success
    : $variant === 'removal'
      ? Main.error
      : $variant === 'updated'
        ? BadgeColor.blue
        : Text.secondary};
  font-size: 12px;
  line-height: 16px;
  padding: ${Space.spHalf} ${Space.spBase};
  white-space: nowrap;
`;

// Difference rows use a fixed width so the label and value columns line up across
// rows; the original/updated rows are regular weight, only the net change row is bold
const DifferenceRow = styled(FlexboxContainer) <any>`
  width: 400px;
  justify-content: space-between;
  align-items: center;
  padding: ${Space.spHalf} 0;
`;

const DifferenceLabel = styled.span`
  font-size: 14px;
  color: ${Text.primary};
`;

const DifferenceValue = styled.span`
  font-size: 16px;
  color: ${Text.primary};
`;

const StruckDifferenceValue = styled(DifferenceValue)`
  color: ${Text.secondary};
  text-decoration: line-through;
`;

const NetChangeValue = styled(TotalValue)<{ $isNegative: boolean }>`
  color: ${({ $isNegative }) => $isNegative ? Main.error : Main.success};
`;

const RemovedSubtotal = styled.span`
  color: ${Main.error};
  text-decoration: line-through;
`;

const CommentText = styled.span`
  font-size: 14px;
  line-height: 18px;
  color: ${Text.primary};
  word-break: break-word;
`;

const DetailLabel = styled.strong`
  font-size: 14px;
  line-height: 16px;
  color: ${Text.primary};
`;

const DetailValue = styled.span`
  font-size: 14px;
  line-height: 20px;
  color: ${Text.primary};
`;

const DetailSubvalue = styled.span`
  font-size: 14px;
  line-height: 16px;
  color: ${Text.secondary};
`;

const NoteIcon = styled(Icon).attrs({ name: 'info', size: 16 })`
  color: ${Main.brandDark};
  flex-shrink: 0;
  margin-top: 1px;
`;

const ActionsBar = styled(FlexboxContainer) <any>`
  border: 1px solid ${Border.light};
  border-radius: ${BorderRadius.m};
  background-color: ${Background.white};
  box-sizing: border-box;
  padding: ${Space.sp2x};
  margin: 0 ${Space.sp2x} ${Space.sp2x};
  gap: ${Space.sp2x};
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
`;

// Keeps the three action buttons at their natural width on one line. Each Button is
// wrapped in a div by ReadOnlyWrapper, so the no-shrink rule has to target that div.
const ActionButtons = styled(FlexboxContainer) <any>`
  flex-shrink: 0;
  flex-wrap: wrap;

  > div {
    flex-shrink: 0;
  }

  button {
    white-space: nowrap;
  }
`;

// Drops to its own row once the space left beside the buttons gets too narrow
const ActionsHint = styled(FlexboxContainer) <any>`
  flex: 1 1 280px;
  min-width: 280px;
`;

// Clickable header of the collapsible "Original order" section
const CollapsibleSectionHeader = styled(FlexboxContainer) <any>`
  cursor: pointer;
  user-select: none;
`;

const CollapsibleHeaderText = styled.span`
  font-size: 12px;
  color: ${Text.secondary};
  margin-left: ${Space.sp3x};
`;

interface Props {
  consignmentDetails: Cons.ConsignmentDetails;
  onActionComplete: () => void;
};

const amountFormatter = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatAmount = (value: number) => amountFormatter.format(value || 0);

const toChangeTag = (modificationType: string): { variant: 'addition' | 'removal' | 'updated' | 'default'; label: string } => {
  switch (modificationType) {
    case EDIT_ORDER_ACTIONS.ADDED:
      return { variant: 'addition', label: TEXT_LABELS.ADDED_TAG };
    case EDIT_ORDER_ACTIONS.CHANGED:
      return { variant: 'updated', label: TEXT_LABELS.UPDATED_ITEM_TAG };
    case EDIT_ORDER_ACTIONS.REMOVED:
      return { variant: 'removal', label: TEXT_LABELS.REMOVED_TAG };
    default:
      return { variant: 'default', label: TEXT_LABELS.UPDATED_ITEM_TAG };
  }
};

/**
 * "Awaiting buyer approval" view of OrderDetails, shown while the consignment is in
 * AWAITS_EDIT_APPROVAL after the seller sent applied edit-order changes to the buyer.
 *
 * The buyer decides in DS, not here; the three actions below are the seller's own routes
 * out of the state. Withdraw reverts the order from the pre-edit snapshot, approve-on-
 * behalf records the buyer's agreement, and resend asks Hybris to email the buyer again.
 * Withdraw and approve confirm first; resend is non-destructive and fires directly.
 *
 * Fetching stays with the parent — `onActionComplete` reloads it once an action lands,
 * which drops the consignment out of AWAITS_EDIT_APPROVAL and unmounts this view.
 */
const AwaitingEditApproval = ({ consignmentDetails, onActionComplete }: Props) => {
  const { countries } = useCountries();
  const [isOriginalOrderExpanded, setIsOriginalOrderExpanded] = useState(false);
  const [showWithdrawConfirm, setShowWithdrawConfirm] = useState(false);
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);

  // Failure toasts use the written copy rather than the server's message: backend errors
  // here are internal state names, and a 500 surfaces `err.message` raw (see
  // api/src/utils/response.js), which can be a database error. The detail is in the
  // tracedLogger entry on the server side.
  const withdrawReq = useAPI({
    deferFn: withdrawEditApproval,
    onResolve: () => {
      renderToast(toast.TYPE.SUCCESS, TEXT_LABELS.WITHDRAW_REQUEST_SUCCESS);
      onActionComplete();
    },
    onReject: () => {
      renderToast(toast.TYPE.ERROR, TEXT_LABELS.WITHDRAW_REQUEST_FAILED);
    }
  });

  const resendReq = useAPI({
    deferFn: resendEditApproval,
    onResolve: () => {
      renderToast(toast.TYPE.SUCCESS, TEXT_LABELS.RESEND_APPROVAL_SUCCESS);
    },
    onReject: () => {
      renderToast(toast.TYPE.ERROR, TEXT_LABELS.RESEND_APPROVAL_FAILED);
    }
  });

  const approveReq = useAPI({
    deferFn: approveEditOrderOnBehalf,
    onResolve: () => {
      renderToast(toast.TYPE.SUCCESS, TEXT_LABELS.APPROVE_ON_BEHALF_SUCCESS);
      onActionComplete();
    },
    onReject: () => {
      renderToast(toast.TYPE.ERROR, TEXT_LABELS.APPROVE_ON_BEHALF_FAILED);
    }
  });

  // All three disable together: they are mutually exclusive outcomes of one decision, so
  // none should be reachable while another is in flight.
  const isActionInFlight = withdrawReq.isLoading || resendReq.isLoading || approveReq.isLoading;
  const { consignmentId } = consignmentDetails;

  const currencyIso = useMemo(() => {
    const countryIso = countries.find(country => country.id === consignmentDetails.countryId)?.countryIso;

    return getCurrencyIsoByCountry(countryIso!);
  }, [countries, consignmentDetails.countryId]);

  const currencySymbol = getCurrencySymbol(currencyIso);

  // The live markers can't be trusted once the order is sent for approval, so the "changes
  // submitted" table diffs the live rows against the open edit-order snapshot instead.
  const changedEntries = useMemo(() =>
    deriveSubmittedChanges(consignmentDetails.consignmentEntries, consignmentDetails.editOrderSnapshotEntries || []),
  [consignmentDetails.consignmentEntries, consignmentDetails.editOrderSnapshotEntries]);

  const originalEntries = useMemo(() => {
    const liveEntryById = new Map(consignmentDetails.consignmentEntries.map(entry => [entry.id, entry]));
    // The EDIT_ORDER snapshot holds the complete pre-edit order (items later changed or
    // removed included); only parent rows are shown, addons are filtered out like elsewhere
    const snapshotEntries = (consignmentDetails.editOrderSnapshotEntries || [])
      .filter(entry => entry.parentEntryNumber == null);

    if (snapshotEntries.length > 0) {
      return snapshotEntries.map(entry => {
        const liveById = liveEntryById.get(entry.consignmentEntryId);
        const live = liveById?.productId === entry.productId ? liveById : undefined;

        return {
          key: entry.consignmentEntryId,
          productName: entry.productName,
          sku: entry.materialNumber || entry.sku || live?.materialNumber || live?.sku || '',
          productImages: entry.productImages?.length ? entry.productImages : live?.productImages,
          quantity: entry.quantity,
          basePrice: entry.basePrice,
          comment: entry.comment
        };
      });
    }

    // No snapshot rows — fall back to the untouched live items
    return consignmentDetails.consignmentEntries
      .filter(entry => !entry.modificationType)
      .map(entry => ({
        key: entry.id,
        productName: entry.productName,
        sku: entry.materialNumber || entry.sku || '',
        productImages: entry.productImages,
        quantity: entry.quantity,
        basePrice: entry.basePrice,
        comment: latestVendorComment(entry.comments)
      }));
  }, [consignmentDetails]);

  const originalTotal = consignmentDetails.editOrderOriginalTotal ?? 0;
  const updatedTotal = consignmentDetails.totalPriceWithTax;
  const netChange = updatedTotal - originalTotal;
  const customer = consignmentDetails.order.customer;

  return (
    <>
      <WarningBanner
        type="warning"
        fontSize="14px"
        margin={`0 ${Space.sp2x} ${Space.sp2x}`}
        message={
          <FlexboxContainer flexDirection="column" alignItems="flex-start">
            <strong>{TEXT_LABELS.AWAITING_BUYER_APPROVAL_TITLE}</strong>
            <span>{TEXT_LABELS.AWAITING_BUYER_APPROVAL_BODY}</span>
            <span>{TEXT_LABELS.AWAITING_BUYER_APPROVAL_HOLD}</span>
          </FlexboxContainer>
        }
      />
      <SectionCard>
        <SectionTitle>{TEXT_LABELS.ORDER_CHANGES_SUBMITTED}</SectionTitle>
        <SectionSubtitle>{TEXT_LABELS.ORDER_CHANGES_SUBMITTED_SUBTITLE}</SectionSubtitle>
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <HeaderCell width="11%">{TEXT_LABELS.CHANGE}</HeaderCell>
                <HeaderCell width="21%">{TEXT_LABELS.PRODUCT}</HeaderCell>
                <HeaderCell width="11%">{TEXT_LABELS.ITEM_ID_SKU}</HeaderCell>
                <HeaderCell width="12%">{`${TEXT_LABELS.UNIT_PRICE} (${currencyIso})`}</HeaderCell>
                <HeaderCell width="8%" align="center">{TEXT_LABELS.QUANTITY}</HeaderCell>
                <HeaderCell width="12%" align="center">{`${TEXT_LABELS.SUBTOTAL} (${currencyIso})`}</HeaderCell>
                <HeaderCell width="25%">{TEXT_LABELS.VENDOR_COMMENT}</HeaderCell>
              </tr>
            </thead>
            <tbody>
              {changedEntries.map(entry => {
                const tag = toChangeTag(entry.modificationType!);
                const isRemoved = entry.modificationType === EDIT_ORDER_ACTIONS.REMOVED;
                const productImage = entry.productImages && entry.productImages.length > 0
                  ? process.env.REACT_APP_MEDIA_URL! + entry.productImages[0].path
                  : '';

                return (
                  <tr key={entry.key}>
                    <BodyCell>
                      <TypeTag $variant={tag.variant}>
                        {tag.label}
                      </TypeTag>
                    </BodyCell>
                    <BodyCell>
                      <FlexboxContainer alignItems="center" gap={Space.spBasePlus}>
                        <ProductImage src={productImage} alt="" />
                        <ProductName>{entry.productName}</ProductName>
                      </FlexboxContainer>
                    </BodyCell>
                    <BodyCell>
                      <SkuText>{entry.materialNumber || entry.sku}</SkuText>
                    </BodyCell>
                    <BodyCell>{`${currencySymbol}${formatAmount(entry.basePrice)}`}</BodyCell>
                    <BodyCell align="center">{entry.quantity}</BodyCell>
                    <BodyCell align="center">
                      {isRemoved ? (
                        <RemovedSubtotal>{`${currencySymbol}${formatAmount(entry.quantity * entry.basePrice)}`}</RemovedSubtotal>
                      ) : (
                        `${currencySymbol}${formatAmount(entry.quantity * entry.basePrice)}`
                      )}
                    </BodyCell>
                    <BodyCell>
                      <CommentText>{entry.vendorComment || '—'}</CommentText>
                    </BodyCell>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </TableWrapper>
        {/* Collapsible original order inside the "Order changes submitted" card — the
            collapsed header is the show prompt, the expanded header the plain label */}
        <CollapsibleSectionHeader
          alignItems="center"
          gap={Space.spBase}
          margin={`${Space.sp2x} 0 0`}
          onClick={() => setIsOriginalOrderExpanded(current => !current)}
        >
          <SectionTitle>{TEXT_LABELS.ORIGINAL_ORDER_TITLE}</SectionTitle>
          <CollapsibleHeaderText>
            {isOriginalOrderExpanded
              ? TEXT_LABELS.ORIGINAL_ORDER_BEFORE_CHANGES
              : TEXT_LABELS.SHOW_ORIGINAL_ORDER_BEFORE_CHANGES}
          </CollapsibleHeaderText>
          <Icon name={isOriginalOrderExpanded ? 'chevron-up' : 'chevron-down'} size={16} />
        </CollapsibleSectionHeader>
        {isOriginalOrderExpanded && (
          <TableWrapper>
          <Table>
            <thead>
              <tr>
                {/* Product leads here (unlike the "Order changes submitted" table above, where
                    "Change" leads) — the spacer below still matches that table's "Change" column
                    width, so SKU through Vendor comment stay aligned between the two tables even
                    though Product itself doesn't. */}
                <HeaderCell width="21%">{TEXT_LABELS.PRODUCT}</HeaderCell>
                <HeaderCell width="11%" />
                <HeaderCell width="11%">{TEXT_LABELS.ITEM_ID_SKU}</HeaderCell>
                <HeaderCell width="12%">{`${TEXT_LABELS.UNIT_PRICE} (${currencyIso})`}</HeaderCell>
                <HeaderCell width="8%" align="center">{TEXT_LABELS.QUANTITY}</HeaderCell>
                <HeaderCell width="12%" align="center">{`${TEXT_LABELS.SUBTOTAL} (${currencyIso})`}</HeaderCell>
                <HeaderCell width="25%">{TEXT_LABELS.VENDOR_COMMENT}</HeaderCell>
              </tr>
            </thead>
            <tbody>
              {originalEntries.map(entry => {
                const productImage = entry.productImages && entry.productImages.length > 0
                  ? process.env.REACT_APP_MEDIA_URL! + entry.productImages[0].path
                  : '';

                return (
                  <tr key={entry.key}>
                    <BodyCell>
                      <FlexboxContainer alignItems="center" gap={Space.spBasePlus}>
                        <ProductImage src={productImage} alt="" />
                        <ProductName>{entry.productName}</ProductName>
                      </FlexboxContainer>
                    </BodyCell>
                    <BodyCell />
                    <BodyCell>
                      <SkuText>{entry.sku}</SkuText>
                    </BodyCell>
                    <BodyCell>{`${currencySymbol}${formatAmount(entry.basePrice)}`}</BodyCell>
                    <BodyCell align="center">{entry.quantity}</BodyCell>
                    <BodyCell align="center">{`${currencySymbol}${formatAmount(entry.quantity * entry.basePrice)}`}</BodyCell>
                    <BodyCell>
                      <CommentText>{entry.comment || '—'}</CommentText>
                    </BodyCell>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </TableWrapper>
        )}
        {/* Original vs updated order difference — kept outside the collapsible content
            so the summary stays visible when the item list is collapsed */}
        <FlexboxContainer flexDirection="column" alignItems="flex-end" margin={`${Space.sp3x} 0 0`}>
          <DifferenceRow>
            <DifferenceLabel>{TEXT_LABELS.ORIGINAL_ORDER_TOTAL}</DifferenceLabel>
            <StruckDifferenceValue>{`${currencySymbol}${formatAmount(originalTotal)}`}</StruckDifferenceValue>
          </DifferenceRow>
          <DifferenceRow>
            <DifferenceLabel>{TEXT_LABELS.UPDATED_ORDER_TOTAL}</DifferenceLabel>
            <DifferenceValue>{`${currencySymbol}${formatAmount(updatedTotal)}`}</DifferenceValue>
          </DifferenceRow>
          <Divider bgColor={Border.light} noMarginX marginY={Space.spHalf} width="400px" />
          <DifferenceRow>
            <TotalLabel>{TEXT_LABELS.NET_CHANGE}</TotalLabel>
            <NetChangeValue $isNegative={netChange < 0}>
              {`${netChange < 0 ? '-' : ''}${currencySymbol}${formatAmount(Math.abs(netChange))}`}
            </NetChangeValue>
          </DifferenceRow>
        </FlexboxContainer>
      </SectionCard>
      <SectionCard>
        <SectionTitle>{TEXT_LABELS.APPROVAL_REQUEST_DETAILS}</SectionTitle>
        <FlexboxContainer justifyContent="space-between" margin={`${Space.sp2x} 0 0`} width='90%'>
          <FlexboxContainer flexDirection="column" alignItems="flex-start" gap={Space.spHalf}>
            <DetailLabel>{TEXT_LABELS.SENT_TO}</DetailLabel>
            <DetailValue>{customer.fullName}</DetailValue>
            <DetailSubvalue>{customer.email}</DetailSubvalue>
          </FlexboxContainer>
          <FlexboxContainer flexDirection="column" alignItems="flex-start" gap={Space.spHalf}>
            <DetailLabel>{TEXT_LABELS.SENT_ON}</DetailLabel>
            <DetailValue>{`${formatDateTime(consignmentDetails.updated, true)} ${getLocalTimezone()}`}</DetailValue>
          </FlexboxContainer>
          <FlexboxContainer flexDirection="column" alignItems="flex-start" gap={Space.spHalf}>
            <DetailLabel>{TEXT_LABELS.STATUS}</DetailLabel>
            <Pill size="xsPlus" borderColor={Main.brandDark} bgColor={Main.newError2} color={Main.brandDark} isUppercase={false}>
              {TEXT_LABELS.WAITING_FOR_APPROVAL}
            </Pill>
            <DetailSubvalue>{TEXT_LABELS.BUYER_NOT_RESPONDED_YET}</DetailSubvalue>
          </FlexboxContainer>
        </FlexboxContainer>
        <FlexboxContainer alignItems="flex-start" gap={Space.spHalfPlus} margin={`${Space.sp2xPlus} 0 0`}>
          <NoteIcon />
          <DetailSubvalue>{TEXT_LABELS.BUYER_NOTIFIED_OF_REQUEST_INFO}</DetailSubvalue>
        </FlexboxContainer>
      </SectionCard>
      <ActionsBar>
        <ActionButtons gap={Space.sp2x}>
          <Button
            isGhost
            theme="error"
            color={Main.error}
            borderColor={Main.error}
            disabled={isActionInFlight}
            onClick={() => setShowWithdrawConfirm(true)}
          >
            {TEXT_LABELS.WITHDRAW_REQUEST}
          </Button>
          <Button
            isGhost
            color={Text.primary}
            borderColor={Border.main}
            disabled={isActionInFlight}
            onClick={() => resendReq.run(consignmentId)}
          >
            {TEXT_LABELS.RESEND_APPROVAL_REQUEST}
          </Button>
          <Button
            theme="brandDark"
            disabled={isActionInFlight}
            onClick={() => setShowApproveConfirm(true)}
            permissionCode={PermissionCode.OVERRIDE_APPROVAL}
          >
            {TEXT_LABELS.APPROVE_ON_BEHALF_OF_BUYER}
          </Button>
        </ActionButtons>
        <ActionsHint gap={Space.spHalfPlus} alignItems="flex-start">
          <NoteIcon />
          <DetailSubvalue>{TEXT_LABELS.WITHDRAW_APPROVAL_REQUEST_HINT}</DetailSubvalue>
        </ActionsHint>
      </ActionsBar>
      <ConfirmationModal
        show={showWithdrawConfirm}
        onClose={() => setShowWithdrawConfirm(false)}
        onConfirm={() => { setShowWithdrawConfirm(false); withdrawReq.run(consignmentId); }}
        title={TEXT_LABELS.WITHDRAW_CONFIRM_TITLE}
        message={TEXT_LABELS.WITHDRAW_CONFIRM_MESSAGE}
        confirmText={TEXT_LABELS.WITHDRAW_CONFIRM_ACTION}
        primaryButtonColor={Main.error}
        disabled={isActionInFlight}
      />
      <ConfirmationModal
        show={showApproveConfirm}
        onClose={() => setShowApproveConfirm(false)}
        onConfirm={() => { setShowApproveConfirm(false); approveReq.run(consignmentId); }}
        title={TEXT_LABELS.APPROVE_ON_BEHALF_CONFIRM_TITLE}
        message={TEXT_LABELS.APPROVE_ON_BEHALF_CONFIRM_MESSAGE}
        confirmText={TEXT_LABELS.APPROVE_ON_BEHALF_CONFIRM_ACTION}
        disabled={isActionInFlight}
      />
    </>
  );
};

export default AwaitingEditApproval;
